import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle, Hexagon, Triangle, Box } from 'lucide-react';
import { heroContent } from '../mock';

const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-white pt-24">
      {/* Background Image - Minimal overlay for modern look */}
      <div className="absolute inset-0 z-0 opacity-20">
        <img 
          src="https://images.pexels.com/photos/10887727/pexels-photo-10887727.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Modern Architecture"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Subtle accent shapes */}
      <div className="absolute top-40 right-20 w-64 h-64 bg-brand-yellow/20 rounded-full blur-3xl z-0"></div>
      <div className="absolute bottom-20 left-40 w-96 h-96 bg-brand-mint/20 rounded-full blur-3xl z-0"></div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-[7.6923%] py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column - Heavy Typography */}
          <div className="lg:col-span-8">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 px-4 py-2 mb-8">
              <CheckCircle className="text-brand-blue" size={18} strokeWidth={2} />
              <span className="font-mono text-xs tracking-widest text-gray-700 uppercase">Trusted by 1500+ Businesses</span>
            </div>

            {/* Headline - Swiss Typography Style */}
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.05] mb-4 tracking-tight">
              Empowering Businesses
              <br />
              with <span className="text-brand-blue">Expert Financial</span>
              <br />
              Solutions
            </h1>
            
            {/* Kannada company name */}
            <div className="text-left mb-8">
              <p className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-1">ಆರ್ ಎಸ್ ಟಿ ಅಂಡ್ ಕಂ</p>
              <p className="font-heading text-lg md:text-xl text-gray-600">ಸನ್ನದು ಲೆಕ್ಕ ಪರಿಶೋಧಕರು</p>
            </div>

            {/* Subheadline */}
            <p className="font-body text-lg md:text-xl text-gray-600 leading-relaxed mb-12 max-w-2xl">
              Your trusted partner for <span className="font-semibold text-gray-900">Foreign Subsidiary formation &amp; compliance, IPO Readiness &amp; ESG Audits, AI/ML compliance and NRI taxation</span>—designed for entrepreneurs, MNCs, and global Indian businesses.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <Button 
                onClick={() => scrollToSection('cta')}
                className="bg-brand-blue hover:bg-brand-blue-hover text-white font-medium px-8 py-6 h-auto text-base transition-all duration-300 group shadow-lg hover:shadow-xl"
                data-testid="hero-cta-primary"
              >
                <span>Schedule a Consultation</span>
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} strokeWidth={2} />
              </Button>
              <Button 
                onClick={() => scrollToSection('services')}
                variant="outline"
                className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-medium px-8 py-6 h-auto text-base transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                Explore Services
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {heroContent.trustBadges.map((badge, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="text-brand-mint flex-shrink-0 mt-1" size={20} strokeWidth={2} />
                  <span className="text-sm text-gray-700 font-medium leading-tight">{badge}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Partner Icons/Abstract Visual */}
          <div className="lg:col-span-4 relative">
            <div className="relative">
              {/* Abstract partner/client representation using geometric icons */}
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center justify-center h-32 bg-gray-50 border border-gray-200 hover:border-brand-blue/50 transition-all group">
                  <Hexagon className="text-brand-blue" size={48} strokeWidth={1.5} />
                </div>
                <div className="flex items-center justify-center h-32 bg-gray-50 border border-gray-200 hover:border-brand-blue/50 transition-all group">
                  <Triangle className="text-brand-mint" size={48} strokeWidth={1.5} />
                </div>
                <div className="flex items-center justify-center h-32 bg-gray-50 border border-gray-200 hover:border-brand-blue/50 transition-all group">
                  <Box className="text-brand-yellow" size={48} strokeWidth={1.5} />
                </div>
                <div className="flex items-center justify-center h-32 bg-gray-50 border border-gray-200 hover:border-brand-blue/50 transition-all group">
                  <Hexagon className="text-gray-700" size={48} strokeWidth={1.5} />
                </div>
              </div>
              
              {/* Accent line */}
              <div className="absolute -bottom-4 left-0 w-full h-1 bg-gradient-to-r from-brand-blue via-brand-mint to-brand-yellow"></div>
            </div>

            {/* Floating stat card */}
            <div className="mt-8 bg-white border-2 border-gray-900 p-6 shadow-lg">
              <p className="font-mono text-xs tracking-widest text-gray-500 uppercase mb-2">Impact</p>
              <p className="font-heading text-4xl font-bold text-gray-900">15+</p>
              <p className="text-sm text-gray-600 mt-1">Years of Excellence</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent z-5"></div>
    </section>
  );
};

export default HeroSection;
